import { grey, lightBlue, orange, red, teal } from '@mui/material/colors';
import { useCallback } from 'react';
import { AdsState } from 'typeDeclarations/graphql/nodes';
import { getObjectKeys } from './getObjectKeys';

export type StateVariant =
  | 'draft'
  | 'paused'
  | 'running'
  | 'archived'
  | 'canceled'
  | 'problems'
  | 'noneRunning'
  | 'someRunning'
  | 'someProblems'
  | 'pendingReview'
  | 'pendingTrackingSetup'
  | 'someRunningSomeProblems';

export const BASE_ADS_STATE_TO_VARIANT_MAP: Record<AdsState, StateVariant> = {
  ALL_DRAFT: 'draft',
  ALL_PAUSED: 'paused',
  ALL_RUNNING: 'running',
  ALL_ARCHIVED: 'archived',
  ALL_CANCELED: 'canceled',
  ALL_PROBLEMS: 'problems',
  NONE_RUNNING: 'noneRunning',
  SOME_RUNNING: 'someRunning',
  SOME_PROBLEMS: 'someProblems',
  ALL_PENDING_REVIEW: 'pendingReview',
  ALL_PENDING_TRACKING_SETUP: 'pendingTrackingSetup',
  SOME_RUNNING_SOME_PROBLEMS: 'someRunningSomeProblems',
};

export const STATE_VARIANT_TO_COLOR: Record<StateVariant, Record<string, string>> = {
  running: teal,
  pendingReview: lightBlue,
  draft: orange,
  paused: orange,
  someRunning: orange,
  someRunningSomeProblems: orange,
  someProblems: red,
  noneRunning: red,
  pendingTrackingSetup: red,
  problems: red,
  archived: grey,
  canceled: grey,
};

const BASE_AD_STATE_FILTERS_MAP: PartialRecord<StateVariant, AdsState[]> = {
  draft: ['ALL_DRAFT'],
  paused: ['ALL_PAUSED'],
  running: ['ALL_RUNNING'],
  canceled: ['ALL_CANCELED'],
  archived: ['ALL_ARCHIVED'],
  problems: ['ALL_PROBLEMS', 'SOME_PROBLEMS'],
  pendingReview: ['ALL_PENDING_REVIEW'],
  pendingTrackingSetup: ['ALL_PENDING_TRACKING_SETUP'],
  someRunning: ['SOME_RUNNING'],
  someRunningSomeProblems: ['SOME_RUNNING_SOME_PROBLEMS'],
  noneRunning: ['ALL_PROBLEMS', 'SOME_PROBLEMS', 'NONE_RUNNING'],
  someProblems: ['SOME_PROBLEMS'],
};

export function useAdStateFilters() {
  const filterLabels = getObjectKeys(BASE_AD_STATE_FILTERS_MAP);

  const getFilterValues = useCallback(
    (stateVariants: StateVariant[]) => {
      // No filters or all filters applied so it's more efficient to not filter by anything
      if (!stateVariants.length || stateVariants.length === filterLabels.length) {
        return undefined;
      }

      return stateVariants.flatMap((label) => BASE_AD_STATE_FILTERS_MAP[label] ?? []);
    },
    [filterLabels],
  );

  return {
    filterLabels,
    getFilterValues,
  };
}
